import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { store } from "./app/store";
import Root from "./routes/Root";
import Error from "./routes/Error";
import Resolve from "./routes/Resolve";
import Admin from "./routes/Admin";
import SuperAdminConfig from "./routes/SuperAdminConfig";
import OrionFlowTypes from "./routes/admin/OrionFlowTypes";
import DocumentAccessLog from "./routes/DocumentAccessLog";
import SignInAttempts from "./routes/SignInAttempts";
import AgenticWmActivity from "./routes/AgenticWmActivity";
import Feedback from "./routes/Feedback";
import WorkHome from "./routes/work/Home";
import WorkClients from "./routes/work/Clients";
import WorkFscClients from "./routes/work/FscClients";
import WorkFscClientDetail from "./routes/work/FscClientDetail";
import WorkClientNew from "./routes/work/ClientNew";
import WorkClientDetail from "./routes/work/ClientDetail";
import WorkDocuments from "./routes/work/Documents";
import WorkNetNewAum from "./routes/work/NetNewAum";
import WorkCrm from "./routes/work/Crm";
import WorkCrmClientDetail from "./routes/work/CrmClientDetail";
import WorkCrmCalendar from "./routes/work/CrmCalendar";
import WorkTasks from "./routes/work/Tasks";
import WorkAgenticWm from "./routes/work/AgenticWm";
import Dashboard from "./routes/Dashboard";
import Executive from "./routes/Executive";
import Annuity from "./routes/Annuity";
import ExecutiveAI from "./routes/ExecutiveAI";
import HR from "./routes/HR";
import HRDirectory from "./routes/HRDirectory";
import HROrgChart from "./routes/HROrgChart";
import HRConfig from "./routes/HRConfig";
import HRHolidays from "./routes/HRHolidays";
import HRJobOpenings from "./routes/HRJobOpenings";
import JobOpenings from "./routes/JobOpenings";
import ManagerApplicants from "./routes/ManagerApplicants";
import RoleAlignment from "./routes/forms/RoleAlignment";
import TeamRoleAlignment from "./routes/forms/TeamRoleAlignment";
import TechRoleAlignment from "./routes/forms/TechRoleAlignment";
import Holidays from "./routes/Holidays";
import Tech from "./routes/Tech";
import TechIntegration from "./routes/TechIntegration";
import TechConfig from "./routes/TechConfig";
import EmailMigration from "./routes/EmailMigration";
import ClaudeAccess from "./routes/ClaudeAccess";
import ClaudeProjectsVideo from "./routes/ClaudeProjectsVideo";
import ClaudeActivations from "./routes/ClaudeActivations";
import B2BClaudeActivations from "./routes/B2BClaudeActivations";
import TrainingCourses from "./routes/TrainingCourses";
import TrainingCourseEditor from "./routes/TrainingCourseEditor";
import TrainingMyCourses from "./routes/TrainingMyCourses";
import TrainingCourseLearner from "./routes/TrainingCourseLearner";
import TrainingResults from "./routes/TrainingResults";
import RegSP from "./routes/RegSP";
import ComplianceQuickHitter from "./routes/ComplianceQuickHitter";
import ComplianceQuickHitterView from "./routes/ComplianceQuickHitterView";
import EmployeeDirectory from "./routes/EmployeeDirectory";
import MyTeam from "./routes/MyTeam";
import YourDay from "./routes/YourDay";
import Meetings from "./routes/Meetings";
import Planner from "./routes/Planner";
import Tasks from "./routes/Tasks";
import Bloom from "./routes/Bloom";
import CalendarPage from "./routes/Calendar";
import Events from "./routes/Events";
import EntrepreneurFirms from "./routes/entrepreneur/Firms";
import EntrepreneurFirmDetail from "./routes/entrepreneur/FirmDetail";
import EntrepreneurClaudeFirms from "./routes/entrepreneur/ClaudeFirms";
import EntrepreneurClaudeFirmDetail from "./routes/entrepreneur/ClaudeFirmDetail";
import EntrepreneurProvisioning from "./routes/entrepreneur/Provisioning";
import EntrepreneurInvoices from "./routes/entrepreneur/Invoices";
import EntrepreneurChargeUsage from "./routes/entrepreneur/ChargeUsage";
import EntrepreneurActionsLog from "./routes/entrepreneur/ActionsLog";
import EntrepreneurInfo from "./routes/entrepreneur/Info";
import EntrepreneurConfig from "./routes/entrepreneur/Config";
import EntrepreneurFscTrainingSignups from "./routes/entrepreneur/FscTrainingSignups";
import EntrepreneurAnnouncements from "./routes/entrepreneur/Announcements";
import EntrepreneurTransitions from "./routes/entrepreneur/Transitions";
import EntrepreneurTransitionEditor from "./routes/entrepreneur/TransitionEditor";
import MarketingResourcesAdmin from "./routes/marketing/Resources";
import MarketingActivity from "./routes/marketing/Activity";
import MarketingResources from "./routes/MarketingResources";
import ResourceLibrary from "./routes/ResourceLibrary";
import MarketingResourceLibrary from "./routes/marketing/ResourceLibrary";
import Cron from "./routes/Cron";
import CeleryJobs from "./routes/CeleryJobs";
import FirmOffices from "./routes/FirmOffices";
import WorkAccess from "./routes/WorkAccess";
import BloomUsers from "./routes/tech/BloomUsers";
import FscTrainingIndex from "./routes/fsc-training/Index";
import { GUIDE_ROUTES as FSC_GUIDE_ROUTES } from "./routes/fsc-training/guides";
import BloomBoard from "./routes/exec/BloomBoard";
import FscSync from "./routes/FscSync";
import OrionSync from "./routes/OrionSync";
import CrmMigration from "./routes/CrmMigration";
import CrmSync from "./routes/CrmSync";
import NonClients from "./routes/NonClients";
import Status from "./routes/Status";

// Client-facing portal. Lives in its own `portal/` directory and is
// mounted as a top-level sibling of <Root />, NOT a child — the portal
// has no employee sidebar, no MSAL/SSO, and no shared Redux store. Keep
// it that way so the whole tree can be lifted into a standalone app if
// we ever split the deployment.
import PortalLogin from "./portal/Login";
import PortalDashboard from "./portal/Dashboard";

// B2B ("entrepreneur") workspace. Same isolation posture as the portal:
// its own `b2b/` directory, localStorage token (no MSAL/Redux), mounted as a
// top-level sibling of <Root />.
import DocusignCallback from "./routes/DocusignCallback";
import DocumentView from "./routes/DocumentView";
import B2BLogin from "./b2b/Login";
import B2BDashboard from "./b2b/Dashboard";

import reportWebVitals from "./reportWebVitals";

import { createBrowserRouter, RouterProvider } from "react-router-dom";
import AuthRequired from "./features/auth/AuthRequired";
import LoginPage from "./routes/LoginPage";
import AuthRedirect from "./routes/AuthRedirect";
import EmailHelp from "./routes/EmailHelp";
import "./index.css";
import 'bootstrap/dist/css/bootstrap.css';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance, setMsalRedirectResult } from './msalConfig';



// Front-end routes
const router = createBrowserRouter([
  // Client portal — sibling of the employee app, not nested. No <Root>
  // sidebar, no AuthRequired wrapper (the portal will manage its own
  // session model once we add tokens). For now the dashboard stub bounces
  // back to /portal/login if it doesn't have a client object in router
  // state, which is sufficient until the session JWT lands.
  {
    path: "/portal/login",
    element: <PortalLogin />,
    errorElement: <Error />,
  },
  {
    path: "/portal/dashboard",
    element: <PortalDashboard />,
    errorElement: <Error />,
  },
  {
    path: "/portal",
    element: <PortalLogin />,
    errorElement: <Error />,
  },
  // B2B workspace — sibling of the employee app, manages its own token
  // session (localStorage) and bounces to /entrepreneur/login when
  // unauthenticated. The URL uses "entrepreneur" (customer-facing wording);
  // code/roles/API stay "B2B" internally.
  {
    path: "/entrepreneur/login",
    element: <B2BLogin />,
    errorElement: <Error />,
  },
  {
    path: "/entrepreneur/dashboard",
    element: <B2BDashboard />,
    errorElement: <Error />,
  },
  {
    path: "/entrepreneur",
    element: <B2BLogin />,
    errorElement: <Error />,
  },
  // DocuSign JWT consent redirect lands here. Public + standalone (no auth,
  // no sidebar) so the browser navigation from DocuSign doesn't bounce to login.
  {
    path: "/docusign/callback",
    element: <DocusignCallback />,
    errorElement: <Error />,
  },
  // Standalone document viewer, opened by FSC's "View Document" button. Public
  // + standalone for the same reason as the DocuSign callback: the caller has
  // no hub session. The one-time token in the path IS the authorization — it is
  // minted only after the scope check, is single-use and expires in 5 minutes.
  // See routes/DocumentView.js for why this exists rather than letting the
  // browser render the PDF.
  {
    path: "/view/:token",
    element: <DocumentView />,
    errorElement: <Error />,
  },
  // FSC Training guides - PUBLIC + standalone (no auth, no sidebar). Linked
  // from the employee Resource Library, the B2B portal and FSC's own resource
  // section, and the B2B and Salesforce readers have no hub session. They are
  // static how-to pages over the FSC case request forms: no API calls and no
  // client data - keep it that way, anything account-specific does not belong
  // here. One route per guide from the registry (routes/fsc-training/guides.js);
  // the bare path is the index of guides. Not to be confused with
  // /entrepreneur/fsc-training, the B2B live-session sign-up.
  {
    path: "/fsc-training",
    element: <FscTrainingIndex />,
    errorElement: <Error />,
  },
  ...FSC_GUIDE_ROUTES.map(({ path, Component }) => ({
    path,
    element: <Component />,
    errorElement: <Error />,
  })),
  {
    path: "/",
    element: <Root />,
    errorElement: <Error />,
    children: [
      {
        index: true,
        element: <Resolve />,
      },
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        path: "auth-redirect",
        element: <AuthRedirect />,
      },
      {
        path: "email-help",
        element: <EmailHelp />,
      },
      {
        element: <AuthRequired />,
        children: [
          { path: "dashboard",       element: <Dashboard /> },
          { path: "claude-access",                element: <ClaudeAccess /> },
          { path: "claude-projects-video",        element: <ClaudeProjectsVideo /> },
          { path: "employee-directory",            element: <EmployeeDirectory /> },
          { path: "my-team",                       element: <MyTeam /> },
          // Employee read-only view of the entrepreneur-firm marketing library.
          // Management lives at /marketing/resources (MARKETING role). Note the
          // two are NOT the same URL prefix on purpose — this hyphenated one is
          // open to every employee, /marketing/* is the gated section.
          // One section per URL, mirroring how the entrepreneur firms browse the
          // same library. The bare path still resolves (old bookmarks, and the
          // nav group's own header) and lands on the first section.
          { path: "marketing-resources",           element: <MarketingResources /> },
          { path: "marketing-resources/:slug",     element: <MarketingResources /> },
          // The Resource Library pages, siblings of the Marketing sections
          // above in the same sidebar group. Same one-page-per-slug
          // shape, and the bare path resolves to the first page for the same
          // reason (old bookmarks, and the nav group's own header).
          { path: "resources",                     element: <ResourceLibrary /> },
          { path: "resources/:slug",               element: <ResourceLibrary /> },
          { path: "your-day",                      element: <YourDay /> },
          { path: "meetings",                      element: <Meetings /> },
          { path: "planner",                       element: <Planner /> },
          // Tasks is At A Glance's third tab AND still its own page — one
          // component, two ways in, so an old bookmark or a link out of an
          // email still lands somewhere. Bloom came the other way: it was that
          // page's third tab and is now a page of its own.
          { path: "tasks",                         element: <Tasks /> },
          { path: "bloom",                         element: <Bloom /> },
          { path: "calendar",                      element: <CalendarPage /> },
          { path: "marketing/resources",           element: <MarketingResourcesAdmin /> },
          // Authoring for the Resource Library pages under Desk → Resource
          // Library. Moved here from /tech/resource-library on 2026-09-18 —
          // MARKETING owns these pages now, and TECH no longer has an
          // authoring surface for them.
          { path: "marketing/resource-library",    element: <MarketingResourceLibrary /> },
          { path: "marketing/activity",            element: <MarketingActivity /> },
          { path: "compliance/claude-activations", element: <ClaudeActivations /> },
          { path: "compliance/b2b-claude-activations", element: <B2BClaudeActivations /> },
          { path: "entrepreneur/claude-activations",   element: <B2BClaudeActivations /> },
          { path: "compliance/training",           element: <TrainingCourses /> },
          { path: "compliance/training-results",   element: <TrainingResults /> },
          { path: "compliance/reg-sp",             element: <RegSP /> },
          { path: "compliance/quick-hitter",       element: <ComplianceQuickHitter /> },
          // Bulletin viewer. Its own route (rather than a modal) so the hub tile
          // and the admin page's Preview can open an issue in a new tab.
          { path: "compliance/quick-hitter/view/:bulletinId", element: <ComplianceQuickHitterView /> },
          { path: "compliance/training/:courseId", element: <TrainingCourseEditor /> },
          { path: "training",                      element: <TrainingMyCourses /> },
          { path: "training/:courseId",            element: <TrainingCourseLearner /> },
          { path: "holidays",         element: <Holidays /> },
          { path: "events",           element: <Events /> },
          { path: "hr/announcements", element: <HR /> },
          { path: "hr/directory",     element: <HRDirectory /> },
          { path: "hr/org-chart",     element: <HROrgChart /> },
          { path: "hr/holidays",      element: <HRHolidays /> },
          { path: "hr/config",        element: <HRConfig /> },
          { path: "hr/job-openings",  element: <HRJobOpenings /> },
          { path: "job-openings",     element: <JobOpenings /> },
          { path: "job-applicants",   element: <ManagerApplicants /> },
          { path: "forms/role-alignment", element: <RoleAlignment /> },
          { path: "forms/team-role-alignment", element: <TeamRoleAlignment /> },
          { path: "tech/forms/role-alignment", element: <TechRoleAlignment /> },
          { path: "tech/projects",    element: <Tech /> },
          { path: "tech/integration", element: <TechIntegration /> },
          { path: "tech/config",      element: <TechConfig /> },
          { path: "tech/email-migration", element: <EmailMigration /> },
          { path: "executive/kpis", element: <Executive /> },
          { path: "finance/annuity", element: <Annuity /> },
          { path: "executive/ai", element: <ExecutiveAI /> },
          { path: "admin",     element: <Admin /> },
          { path: "admin/config", element: <SuperAdminConfig /> },
          { path: "admin/config/flow-types", element: <OrionFlowTypes /> },
          { path: "admin/document-access-log", element: <DocumentAccessLog /> },
          { path: "admin/sign-in-attempts", element: <SignInAttempts /> },
          { path: "admin/agentic-wm", element: <AgenticWmActivity /> },
          { path: "admin/feedback", element: <Feedback /> },
          { path: "cron",      element: <Cron /> },
          { path: "admin/celery-jobs", element: <CeleryJobs /> },
          { path: "admin/firm-offices", element: <FirmOffices /> },
          { path: "admin/work-access", element: <WorkAccess /> },
          { path: "admin/non-clients", element: <NonClients /> },
          { path: "tech/bloom-users", element: <BloomUsers /> },
          { path: "executive/bloom-board", element: <BloomBoard /> },
          { path: "admin/fsc-sync", element: <FscSync /> },
          { path: "admin/orion-sync", element: <OrionSync /> },
          { path: "admin/crm-migration", element: <CrmMigration /> },
          { path: "admin/crm-sync", element: <CrmSync /> },
          { path: "work",                element: <WorkHome /> },
          { path: "work/clients",        element: <WorkClients /> },
          { path: "work/clients/new",    element: <WorkClientNew /> },
          { path: "work/clients/fsc",     element: <WorkFscClients /> },
          { path: "work/clients/fsc/:fscId", element: <WorkFscClientDetail /> },
          { path: "work/clients/:publicId", element: <WorkClientDetail /> },
          { path: "work/documents",      element: <WorkDocuments /> },
          { path: "work/reports/aum",    element: <WorkNetNewAum /> },
          { path: "work/crm",            element: <WorkCrm /> },
          { path: "work/crm/calendar",   element: <WorkCrmCalendar /> },
          { path: "work/tasks",          element: <WorkTasks /> },
          { path: "work/crm/:clientId",  element: <WorkCrmClientDetail /> },
          { path: "work/agentic-wm",           element: <WorkAgenticWm /> },
          { path: "work/agentic-wm/:threadId", element: <WorkAgenticWm /> },
          { path: "entrepreneur/firms",          element: <EntrepreneurFirms /> },
          { path: "entrepreneur/firms/:firmId",  element: <EntrepreneurFirmDetail /> },
          { path: "entrepreneur/claude-firms",         element: <EntrepreneurClaudeFirms /> },
          { path: "entrepreneur/claude-firms/:firmId", element: <EntrepreneurClaudeFirmDetail /> },
          { path: "entrepreneur/provisioning",   element: <EntrepreneurProvisioning /> },
          { path: "entrepreneur/charge-usage",   element: <EntrepreneurChargeUsage /> },
          { path: "entrepreneur/invoices",       element: <EntrepreneurInvoices /> },
          { path: "entrepreneur/actions-log",    element: <EntrepreneurActionsLog /> },
          { path: "entrepreneur/info",           element: <EntrepreneurInfo /> },
          { path: "entrepreneur/config",         element: <EntrepreneurConfig /> },
          { path: "entrepreneur/fsc-training",   element: <EntrepreneurFscTrainingSignups /> },
          { path: "entrepreneur/announcements",  element: <EntrepreneurAnnouncements /> },
          { path: "entrepreneur/transitions",    element: <EntrepreneurTransitions /> },
          { path: "entrepreneur/transitions/:firmId", element: <EntrepreneurTransitionEditor /> },
          { path: "status",    element: <Status /> },
        ],
      },
    ],
  },
]);

const container = document.getElementById("root");
const root = createRoot(container);

// Are we the popup that Microsoft just redirected back to sign-in?
//
// `acquireTokenPopup` sends the popup to our registered redirectUri
// (/auth-redirect), which is a real SPA route — so without this guard the popup
// boots the whole React app, that app's own handleRedirectPromise() consumes the
// response hash, and the PARENT window's poller never sees it. The popup then
// never closes and the user is left using the hub inside it. From there every
// later acquireTokenPopup fails with MSAL's `block_nested_popups`, because MSAL
// correctly notices it is already running in a popup.
//
// The fix is to do nothing at all in that window: MSAL's parent reads the hash
// straight off popupWindow.location and closes it. Deliberately narrow — it must
// be an opened window AND sitting on the auth route AND carrying a response — so
// the hub opened in a normal window.open() still renders.
const isAuthPopup = (() => {
  try {
    const opened = !!window.opener && window.opener !== window;
    const onAuthRoute = window.location.pathname.startsWith('/auth-redirect');
    const hasResponse = /[#&?](code|error|id_token|state)=/.test(
      window.location.hash + window.location.search
    );
    return opened && onAuthRoute && hasResponse;
  } catch (e) {
    return false; // cross-origin opener access can throw; assume normal window
  }
})();

if (isAuthPopup) {
  console.log('[MSAL] auth popup detected — leaving the response for the parent window');
} else {
  msalInstance.initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((result) => {
    console.log('[MSAL] redirect result:', result);
    setMsalRedirectResult(result);
    root.render(
      <MsalProvider instance={msalInstance}>
        <React.StrictMode>
          <Provider store={store}>
            <RouterProvider router={router} />
          </Provider>
        </React.StrictMode>
      </MsalProvider>
    );
  })
  .catch((err) => {
    console.error('[MSAL] init/redirect error:', err);
    setMsalRedirectResult(null);
    root.render(
      <MsalProvider instance={msalInstance}>
        <React.StrictMode>
          <Provider store={store}>
            <RouterProvider router={router} />
          </Provider>
        </React.StrictMode>
      </MsalProvider>
    );
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
